import React, { useState, useEffect } from "react"
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react"
import { IoChevronDownOutline } from "react-icons/io5"
import Datepicker from "react-tailwindcss-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import useFetchSettings from "../../hooks/useFetchSettings"

const NavButtons = ({
	selectedLocation,
	setSelectedLocation,
	dateRange,
	setDateRange,
	selectedDays,
	setSelectedDays,
	className,
	centers,
	MIN_DATE,
	MAX_DATE
}) => {
	const daysOfWeek = [
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
		"Sunday"
	]
	const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]

	const [isMenuOpen, setIsMenuOpen] = useState(false) // State to control dropdown visibility
	const [localDateRange, setLocalDateRange] = useState(dateRange)

	const { settingsData, settingsStatus, settingsError } = useFetchSettings()

	// Initialize selectedDays based on centerOpen
	useEffect(() => {
		if (settingsData && settingsData.centerOpen) {
			setSelectedDays(daysOfWeek.filter((_, index) => settingsData.centerOpen[index])) // Set the default selected days to the open days
		}
		// React setState setters are stable — omitting deps that are setter-only or initial-load reads.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Initialize date range if not set
	useEffect(() => {
		if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
			const defaultStartDate = new Date()
			defaultStartDate.setMonth(defaultStartDate.getMonth() - 1)
			setDateRange({
				startDate: defaultStartDate,
				endDate: new Date()
			})
			setLocalDateRange({
				startDate: defaultStartDate,
				endDate: new Date()
			})
		}
		// React setState setters are stable — omitting deps that are setter-only or initial-load reads.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Update local state when prop changes
	useEffect(() => {
		setLocalDateRange(dateRange)
	}, [dateRange])

	const handleDaysSelect = day => {
		if (selectedDays.includes(day)) {
			setSelectedDays(selectedDays.filter(d => d !== day))
		} else {
			setSelectedDays([...selectedDays, day])
		}
	}

	// Handle selecting/deselecting all days
	const handleSelectAll = () => {
		if (
			selectedDays.length ===
			daysOfWeek.filter((_, index) => settingsData.centerOpen[index]).length
		) {
			setSelectedDays([]) // Deselect all
		} else {
			setSelectedDays(daysOfWeek.filter((_, index) => settingsData.centerOpen[index])) // Select all
		}
	}

	// Format the selected days for the button label
	const formattedSelectedDays =
		selectedDays.length === 0
			? "Select Days"
			: selectedDays.length ===
				  daysOfWeek.filter((_, index) => settingsData.centerOpen[index]).length
				? "All Days"
				: selectedDays.length === 5 &&
					  daysOfWeek.slice(0, 5).every(day => selectedDays.includes(day))
					? "Weekdays (Mon - Fri)"
					: selectedDays.length === 2 &&
						  daysOfWeek.slice(5).every(day => selectedDays.includes(day))
						? "Weekends (Sat - Sun)"
						: selectedDays.join(", ")

	// useEffect to auto-select weekdays if dropdown is closed and no days are selected
	useEffect(() => {
		if (!isMenuOpen && selectedDays.length === 0 && settingsData) {
			setSelectedDays(weekdays.filter((_, index) => settingsData.centerOpen[index])) // Automatically select weekdays
		}
		// React setState setters are stable — omitting deps that are setter-only or initial-load reads.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMenuOpen, selectedDays, settingsData])

	if (
		!settingsData ||
		settingsData.length === 0 ||
		settingsStatus === "loading" ||
		settingsError
	) {
		return null
	}

	return (
		<>
			<div className={`flex flex-wrap gap-4 relative z-20 ${className}`}>
				<div className="relative">
					<Menu as="div" className="relative inline-block text-left">
						<div>
							<MenuButton className="inline-flex items-center justify-center w-full rounded-md bg-gray-100 text-gray-800 px-4 py-2 shadow-sm hover:bg-gray-200">
								{selectedLocation}
								<IoChevronDownOutline className="w-5 h-5 text-gray-400 ml-2" />
							</MenuButton>
						</div>
						<Transition
							enter="transition duration-100 ease-out"
							enterFrom="transform scale-95 opacity-0"
							enterTo="transform scale-100 opacity-100"
							leave="transition duration-75 ease-in"
							leaveFrom="transform scale-100 opacity-100"
							leaveTo="transform scale-95 opacity-0"
							as="div"
						>
							<MenuItems className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg focus:outline-none">
								<div className="py-1">
									{centers.map(center => (
										<MenuItem key={center.name} as="div">
											{({ active }) => (
												<button
													onClick={() => setSelectedLocation(center.name)}
													className={`${
														active
															? "bg-gray-100 text-gray-900"
															: "text-gray-700"
													} block px-4 py-2 w-full text-left`}
												>
													{center.name}
												</button>
											)}
										</MenuItem>
									))}
								</div>
							</MenuItems>
						</Transition>
					</Menu>
				</div>
				<div className="w-[17rem]">
					<Datepicker
						startWeekOn="mon"
						value={localDateRange}
						onChange={newValue => {
							setLocalDateRange(newValue)
							setDateRange(newValue)
						}}
						showShortcuts={true}
						showFooter={true}
						separator="to"
						displayFormat="YYYY-MM-DD"
						popoverDirection="down"
						minDate={MIN_DATE}
						maxDate={MAX_DATE}
						primaryColor={"indigo"}
						configs={{
							shortcuts: {
								lastWeek: {
									text: "Last Week",
									period: {
										start: new Date(
											new Date().setDate(new Date().getDate() - 7)
										),
										end: new Date()
									}
								},
								lastMonth: {
									text: "Last Month",
									period: {
										start: new Date(
											new Date().setMonth(new Date().getMonth() - 1)
										),
										end: new Date()
									}
								},
								lastSixMonths: {
									text: "Last 6 Months",
									period: {
										start: new Date(
											new Date().setMonth(new Date().getMonth() - 6)
										),
										end: new Date()
									}
								},
								lastYear: {
									text: "Last Year",
									period: {
										start: new Date(
											new Date().setFullYear(new Date().getFullYear() - 1)
										),
										end: new Date()
									}
								}
							}
						}}
						useRange={true}
						readOnly={false}
						disabled={false}
						inputClassName="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
					/>
				</div>

				<div className="relative">
					<Menu as="div" className="relative inline-block text-left">
						<MenuButton
							className="inline-flex items-center justify-center w-full rounded-md bg-gray-100 text-gray-800 px-4 py-2 shadow-sm hover:bg-gray-200"
							onClick={() => setIsMenuOpen(!isMenuOpen)}
						>
							{formattedSelectedDays}
							<IoChevronDownOutline className="w-5 h-5 text-gray-400 ml-2" />
						</MenuButton>

						<Transition
							show={isMenuOpen}
							enter="transition duration-100 ease-out"
							enterFrom="transform scale-95 opacity-0"
							enterTo="transform scale-100 opacity-100"
							leave="transition duration-75 ease-in"
							leaveFrom="transform scale-100 opacity-100"
							leaveTo="transform scale-95 opacity-0"
							as="div"
						>
							<MenuItems
								as="div"
								className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg focus:outline-none"
								static
							>
								<div className="py-1">
									<div className="px-4 py-2 w-full text-left cursor-pointer">
										<label className="flex items-center space-x-2">
											<input
												type="checkbox"
												className="form-checkbox h-4 w-4 text-customBlue rounded border-gray-300 focus:ring-0"
												checked={
													selectedDays.length ===
													daysOfWeek.filter(
														(_, index) => settingsData.centerOpen[index]
													).length
												}
												onChange={handleSelectAll}
											/>
											<span className="text-gray-800">
												{selectedDays.length ===
												daysOfWeek.filter(
													(_, index) => settingsData.centerOpen[index]
												).length
													? "Deselect All"
													: "Select All"}
											</span>
										</label>
									</div>

									<hr className="my-2" />

									{daysOfWeek.map((day, index) => (
										<div
											key={day}
											className={`px-4 py-2 w-full text-left cursor-pointer ${
												!settingsData.centerOpen[index]
													? "opacity-50 cursor-not-allowed"
													: "" // Apply styles if day is not open
											}`}
										>
											<label className="flex items-center space-x-2">
												<input
													type="checkbox"
													className="form-checkbox h-4 w-4 text-customBlue rounded border-gray-300 focus:ring-0"
													checked={
														selectedDays.includes(day) &&
														settingsData.centerOpen[index]
													}
													disabled={!settingsData.centerOpen[index]} // Disable checkbox if day is not open
													onChange={() => handleDaysSelect(day)}
												/>
												<span className="text-gray-800">{day}</span>
											</label>
										</div>
									))}
								</div>
							</MenuItems>
						</Transition>
					</Menu>
				</div>
			</div>
		</>
	)
}

export default NavButtons
